import {put, call} from 'redux-saga/effects';
import FlatFeeMilestone from "../../entities/flatFeeMilestone";
import * as Actions from "../actions/flatFeeMilestoneActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type FlatFeeMilestoneAction = {
    flatFeeMilestoneId: string;
    flatFeeMilestone: FlatFeeMilestone;
    flatFeeMilestones: FlatFeeMilestone[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneFlatFeeMilestone(action: FlatFeeMilestoneAction) {
	const session = tabSession.captureGeneration();
	try {
		const flatFeeMilestone: FlatFeeMilestone = yield call(api.findOne, 'flatFeeMilestone', action.flatFeeMilestoneId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.FLATFEEMILESTONE_FETCH_ONE_SUCCESS, flatFeeMilestoneId: action.flatFeeMilestoneId, flatFeeMilestone});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEEMILESTONE_FETCH_ONE_FAILURE, flatFeeMilestoneId: action.flatFeeMilestoneId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {FlatFeeMilestoneAction} action
 */
export function* saveFlatFeeMilestone(action: FlatFeeMilestoneAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ FLATFEEMILESTONE_SAVE saga', action.flatFeeMilestone?.id);
    
	if (action.flatFeeMilestone === undefined || action.flatFeeMilestone === null) {
		console.error('saveFlatFeeMilestone received a null');
		throw new Error('saveFlatFeeMilestone received a null');
	}

	if (!(action.flatFeeMilestone instanceof FlatFeeMilestone)) {
		action.flatFeeMilestone = new FlatFeeMilestone(action.flatFeeMilestone);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'flatFeeMilestone', action.flatFeeMilestone);
		if (addResult.isErr()) {
			console.error(`⮂ saveFlatFeeMilestone: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.FLATFEEMILESTONE_SAVE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.FLATFEEMILESTONE_SAVE, flatFeeMilestone: action.flatFeeMilestone});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.FLATFEEMILESTONE_SAVE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {FlatFeeMilestoneAction} action
 * @param {FlatFeeMilestone[]} action.flatFeeMilestones
 */
export function* saveManyFlatFeeMilestones(action: FlatFeeMilestoneAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ FLATFEEMILESTONE_SAVE_MANY saga', action.flatFeeMilestones.map(flatFeeMilestone => flatFeeMilestone.id).join(','));
    
    if (!Array.isArray(action.flatFeeMilestones)) {
        console.error('saveManyFlatFeeMilestones expected an array, received', action.flatFeeMilestones);
        throw new Error('saveManyFlatFeeMilestones expected an array');
    }

	try {
		const toSave: FlatFeeMilestone[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.flatFeeMilestones.length; i++) {
			const flatFeeMilestone = action.flatFeeMilestones[i];
			flatFeeMilestone.persisted = false;
			toSave.push(flatFeeMilestone);
		}

		const addResult = yield call(idb.add, 'flatFeeMilestone', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyFlatFeeMilestones: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.FLATFEEMILESTONE_SAVE_MANY_FAILURE, flatFeeMilestones: action.flatFeeMilestones, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.FLATFEEMILESTONE_SAVE_MANY, flatFeeMilestones: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.FLATFEEMILESTONE_SAVE_MANY_FAILURE, flatFeeMilestones: action.flatFeeMilestones, status: 500, err});

		console.error(`Error saving ${action.flatFeeMilestones.length} flatFeeMilestones to database`, err);
	}
}

/**
 * @param {FlatFeeMilestoneAction} action
 */
export function* removeFlatFeeMilestone(action: FlatFeeMilestoneAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.flatFeeMilestone === undefined || action.flatFeeMilestone === null || !(action.flatFeeMilestone instanceof FlatFeeMilestone)) {
			console.error('removeFlatFeeMilestone expected type FlatFeeMilestone, received', action.flatFeeMilestone);
			throw new Error('removeFlatFeeMilestone expected type FlatFeeMilestone');
		}

	try {
		// Mark as deleted
		action.flatFeeMilestone.status = 'D';
		action.flatFeeMilestone.addedOn = newHRTimestamp();
		action.flatFeeMilestone.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'flatFeeMilestone', action.flatFeeMilestone);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeFlatFeeMilestone: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FLATFEEMILESTONE_REMOVE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FLATFEEMILESTONE_REMOVE, flatFeeMilestone: action.flatFeeMilestone});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEEMILESTONE_REMOVE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this FlatFeeMilestone.
	*
	* @param {FlatFeeMilestoneAction} action
	*/
export function* restoreFlatFeeMilestone(action: FlatFeeMilestoneAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreFlatFeeMilestone expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.flatFeeMilestone === undefined || action.flatFeeMilestone === null) {
        console.error('restoreFlatFeeMilestone expected a FlatFeeMilestone, received null');
        throw new Error('restoreFlatFeeMilestone expected a FlatFeeMilestone, received null');
    }
    else if (!(action.flatFeeMilestone instanceof FlatFeeMilestone)) action.flatFeeMilestone = new FlatFeeMilestone(action.flatFeeMilestone); 
    
	try {
		// Mark as restored
		action.flatFeeMilestone.status = 'U';
		action.flatFeeMilestone.addedOn = newHRTimestamp();
		action.flatFeeMilestone.addedBy = userId;
		action.flatFeeMilestone.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'flatFeeMilestone', action.flatFeeMilestone);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreFlatFeeMilestone: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FLATFEEMILESTONE_RESTORE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FLATFEEMILESTONE_RESTORE, flatFeeMilestone: action.flatFeeMilestone});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEEMILESTONE_RESTORE_FAILURE, flatFeeMilestone: action.flatFeeMilestone, status: 500, err});

		console.error('Error restoring from database', err);
	}
}